import React, { useRef, useState } from 'react';

import style from './style.module.scss';

export type TooltipPlacement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end';

export interface TooltipProps {
  children: React.ReactElement<any>;
  text: React.ReactNode;
  placement?: TooltipPlacement;
  className?: string;
}

const placementClasses: { [key in TooltipPlacement]: string } = {
  top: style.tooltipTop,
  'top-start': style.tooltipTopStart,
  'top-end': style.tooltipTopEnd,
  bottom: style.tooltipBottom,
  'bottom-start': style.tooltipBottomStart,
  'bottom-end': style.tooltipBottomEnd,
};

let tooltipSequence = 0;

function createTooltipId(): string {
  tooltipSequence += 1;
  return `pgm-tooltip-${tooltipSequence}`;
}

export default function Tooltip({
  children,
  text,
  placement = 'top',
  className = '',
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const tooltipId = useRef<string | null>(null);

  if (tooltipId.current === null) {
    tooltipId.current = createTooltipId();
  }

  const currentTooltipId = tooltipId.current;
  const classes = [
    style.tooltip,
    placementClasses[placement],
    open ? style.tooltipOpen : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  const describedBy = [children.props['aria-describedby'], currentTooltipId]
    .filter(Boolean)
    .join(' ');
  const anchor = React.cloneElement(children, {
    'aria-describedby': describedBy,
  });

  const handleBlur = (event: React.FocusEvent<HTMLSpanElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setOpen(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <span
      className={classes}
      onBlur={handleBlur}
      onFocus={() => setOpen(true)}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {anchor}
      <span
        aria-hidden={!open}
        className={style.tooltipSurface}
        id={currentTooltipId}
        role="tooltip"
      >
        {text}
      </span>
    </span>
  );
}
